import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PrescriptionForm } from "./PrescriptionForm";
import { ChevronDown, X, Printer } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import type { CustomerData, PrescriptionData } from "@/lib/printer";
import {
  generateESCPOS,
  generateBasicInfoESCPOS,
  generatePrescriptionESCPOS,
} from "@/lib/printer";
import { forwardToPrinter, createCustomer } from "@/lib/trpc-client";
import { getConfig } from "@/lib/config";

// localStorage key for draft persistence
const DRAFT_STORAGE_KEY = "lensflow.customerFormDraft.v1";

// Default empty form data
const DEFAULT_FORM_DATA: CustomerData = {
  customer_name: "",
  phone: "",
  customer_notes: "",
  prescription: {},
};

interface CustomerFormProps {
  pageTitle: string;
  isOnline: boolean;
  onSaveSuccess?: () => void;
}

// Helper to load draft from localStorage (used for lazy initialization)
function loadDraftFromStorage(): CustomerData {
  if (typeof window === "undefined") return DEFAULT_FORM_DATA;
  try {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === "object") {
        return {
          ...DEFAULT_FORM_DATA,
          ...parsed,
          prescription: { ...DEFAULT_FORM_DATA.prescription, ...parsed.prescription },
        };
      }
    }
  } catch {
    // If parsing fails, remove corrupted data
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  }
  return DEFAULT_FORM_DATA;
}

export function CustomerForm({
  pageTitle,
  isOnline,
  onSaveSuccess,
}: CustomerFormProps) {
  // Lazy initialization: read from localStorage synchronously before first render
  const [formData, setFormData] = useState<CustomerData>(loadDraftFromStorage);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [hidePrescript, setHidePrescript] = useState(false);

  // Track if this is the initial mount to skip persisting the initial value
  const isInitialMount = useRef(true);

  const config = getConfig();
  const readyToPrint = Boolean(config.desktopPeerId && config.printerIP);

  // Persist draft to localStorage whenever formData changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
    } catch {
      // Ignore quota errors or private mode restrictions
    }
  }, [formData]);

  // Functional update to avoid stale closure issues
  const updateField = <K extends keyof CustomerData>(
    field: K,
    value: CustomerData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Functional update for prescription
  const updatePrescription = (prescription: PrescriptionData) => {
    setFormData((prev) => ({ ...prev, prescription }));
  };

  // 检查是否有任何数据可以保存或打印
  const hasAnyData = (): boolean => {
    // 检查顾客基本信息
    if (
      formData.customer_name ||
      formData.phone ||
      formData.gender !== undefined ||
      formData.date_of_birth ||
      formData.age_years !== undefined ||
      (formData.customer_notes && formData.customer_notes.trim() !== "")
    ) {
      return true;
    }

    // 检查验光单数据
    if (formData.prescription) {
      const p = formData.prescription;
      if (
        p.use_for !== undefined ||
        p.vertex_distance !== undefined ||
        p.right_sphere !== undefined ||
        p.right_cylinder !== undefined ||
        p.right_cylinder_axis !== undefined ||
        p.right_corrected_vision !== undefined ||
        p.right_papillary_distance !== undefined ||
        p.right_ocular_center_height !== undefined ||
        p.right_add !== undefined ||
        p.left_sphere !== undefined ||
        p.left_cylinder !== undefined ||
        p.left_cylinder_axis !== undefined ||
        p.left_corrected_vision !== undefined ||
        p.left_papillary_distance !== undefined ||
        p.left_ocular_center_height !== undefined ||
        p.left_add !== undefined ||
        p.notes
      ) {
        return true;
      }
    }

    return false;
  };

  const handleSave = async () => {
    if (!hasAnyData()) {
      setMessage({ type: "error", text: "请至少填写一项数据" });
      return;
    }

    if (!config.desktopPeerId) {
      setMessage({ type: "error", text: "请先在配置页面设置桌面端 Peer ID" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await createCustomer(config.desktopPeerId, formData);
      setMessage({ type: "success", text: "保存成功" });

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "保存失败",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!config.desktopPeerId) {
      setMessage({ type: "error", text: "请先在配置页面设置桌面端 Peer ID" });
      return;
    }
    if (!config.printerIP) {
      setMessage({ type: "error", text: "请先在配置页面设置打印机IP地址" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // 先保存数据
      await createCustomer(config.desktopPeerId, formData);

      // 再打印
      const pd = formData.prescription?.right_papillary_distance;
      const escposData = await generateESCPOS(
        formData,
        pageTitle,
        pd,
        config.logoURL,
        config.appendText
      );
      await forwardToPrinter(
        config.desktopPeerId,
        config.printerIP,
        escposData
      );
      setMessage({ type: "success", text: "保存并打印成功" });

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "保存或打印失败",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(DEFAULT_FORM_DATA);
    // Also remove draft from localStorage
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch {
        // Ignore errors
      }
    }
  };

  // 检查是否有基本信息
  const hasBasicInfo = (): boolean => {
    return !!(
      formData.customer_name ||
      formData.phone ||
      formData.gender !== undefined ||
      formData.date_of_birth ||
      formData.age_years !== undefined ||
      (formData.customer_notes && formData.customer_notes.trim() !== "")
    );
  };

  // 检查是否有验光单数据
  const hasPrescriptionData = (): boolean => {
    if (!formData.prescription) return false;
    const p = formData.prescription;
    return !!(
      p.use_for !== undefined ||
      p.vertex_distance !== undefined ||
      p.right_sphere !== undefined ||
      p.right_cylinder !== undefined ||
      p.right_cylinder_axis !== undefined ||
      p.right_corrected_vision !== undefined ||
      p.right_papillary_distance !== undefined ||
      p.right_ocular_center_height !== undefined ||
      p.right_add !== undefined ||
      p.left_sphere !== undefined ||
      p.left_cylinder !== undefined ||
      p.left_cylinder_axis !== undefined ||
      p.left_corrected_vision !== undefined ||
      p.left_papillary_distance !== undefined ||
      p.left_ocular_center_height !== undefined ||
      p.left_add !== undefined ||
      p.notes
    );
  };

  // 打印基本信息
  const handlePrintBasicInfo = async () => {
    if (!config.desktopPeerId) {
      setMessage({ type: "error", text: "请先在配置页面设置桌面端 Peer ID" });
      return;
    }
    if (!config.printerIP) {
      setMessage({ type: "error", text: "请先在配置页面设置打印机IP地址" });
      return;
    }

    if (!hasBasicInfo()) {
      setMessage({ type: "error", text: "请至少填写一项基本信息" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const escposData = await generateBasicInfoESCPOS(
        formData,
        pageTitle,
        config.logoURL
      );
      await forwardToPrinter(
        config.desktopPeerId,
        config.printerIP,
        escposData
      );
      setMessage({ type: "success", text: "基本信息已打印" });
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "打印失败",
      });
    } finally {
      setLoading(false);
    }
  };

  // 打印验光单数据
  const handlePrintPrescription = async () => {
    if (!config.desktopPeerId) {
      setMessage({ type: "error", text: "请先在配置页面设置桌面端 Peer ID" });
      return;
    }
    if (!config.printerIP) {
      setMessage({ type: "error", text: "请先在配置页面设置打印机IP地址" });
      return;
    }

    if (!hasPrescriptionData()) {
      setMessage({ type: "error", text: "请至少填写一项验光单数据" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const pd = formData.prescription?.right_papillary_distance;
      const escposData = await generatePrescriptionESCPOS(
        formData,
        pageTitle,
        pd,
        config.logoURL
      );
      await forwardToPrinter(
        config.desktopPeerId,
        config.printerIP,
        escposData
      );
      setMessage({ type: "success", text: "验光单数据已打印" });
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "打印失败",
      });
    } finally {
      setLoading(false);
    }
  };

  // 仅打印（不保存）
  const handlePrintOnly = async () => {
    if (!config.desktopPeerId) {
      setMessage({ type: "error", text: "请先在配置页面设置桌面端 Peer ID" });
      return;
    }
    if (!config.printerIP) {
      setMessage({ type: "error", text: "请先在配置页面设置打印机IP地址" });
      return;
    }

    if (!hasAnyData()) {
      setMessage({ type: "error", text: "请至少填写一项数据" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const pd = formData.prescription?.right_papillary_distance;
      const escposData = await generateESCPOS(
        formData,
        pageTitle,
        pd,
        config.logoURL,
        config.appendText
      );
      await forwardToPrinter(
        config.desktopPeerId,
        config.printerIP,
        escposData
      );
      setMessage({ type: "success", text: "打印成功" });
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "打印失败",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      {/* 基本信息 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>基本信息</CardTitle>
              <CardDescription>请填写顾客的基本信息</CardDescription>
            </div>
            <Button
              onClick={handlePrintBasicInfo}
              disabled={loading || !readyToPrint || !hasBasicInfo()}
              variant="outline"
              size="sm"
              className="ml-4"
            >
              <Printer className="size-4" />
              打印
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer_name">姓名</Label>
            <Input
              id="customer_name"
              data-1p-ignore="true"
              value={formData.customer_name || ""}
              onChange={(e) => updateField("customer_name", e.target.value)}
              placeholder="视光档案拥有者的名字"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">手机号</Label>
            <Input
              id="phone"
              type="tel"
              data-1p-ignore="true"
              value={formData.phone || ""}
              onChange={(e) => updateField("phone", e.target.value)}
              placeholder="接收通知的手机号码"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender">性别</Label>
            <select
              id="gender"
              className="flex h-10 w-[300px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={
                formData.gender !== undefined && formData.gender !== null
                  ? String(formData.gender)
                  : ""
              }
              onChange={(e) =>
                updateField(
                  "gender",
                  e.target.value === ""
                    ? undefined
                    : parseInt(e.target.value, 10)
                )
              }
            >
              <option value="">请选择</option>
              <option value="0">男</option>
              <option value="1">女</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_of_birth">生日</Label>
            <Input
              className="w-[300px]"
              id="date_of_birth"
              type="date"
              value={formData.date_of_birth || ""}
              onChange={(e) =>
                updateField("date_of_birth", e.target.value || undefined)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_notes">备注</Label>
            <Textarea
              id="customer_notes"
              value={formData.customer_notes || ""}
              onChange={(e) =>
                updateField("customer_notes", e.target.value || undefined)
              }
              placeholder="该顾客的特殊说明、联系方式备注等"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* 可折叠的验光单部分 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div
              className="flex items-center justify-center cursor-pointer select-none flex-1"
              onClick={() => setHidePrescript(!hidePrescript)}
            >
              <ChevronDown
                className={`h-6 w-6 transition-transform duration-200 ${
                  hidePrescript ? "" : "rotate-180"
                }`}
              />
            </div>
            <Button
              onClick={handlePrintPrescription}
              disabled={loading || !readyToPrint || !hasPrescriptionData()}
              variant="outline"
              size="sm"
              className="ml-4"
            >
              <Printer className="size-4" />
              打印
            </Button>
          </div>
        </CardHeader>
        {!hidePrescript && (
          <CardContent>
            <PrescriptionForm
              prescription={formData.prescription || {}}
              onChange={updatePrescription}
            />
          </CardContent>
        )}
      </Card>

      {/* 操作按钮 */}
      <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-lg border">
        <Button
          onClick={handlePrint}
          disabled={loading || !readyToPrint || !hasAnyData()}
          className="flex-1"
          size="lg"
        >
          保存并打印
        </Button>
        <div className="flex flex-1 gap-3">
          <Button
            onClick={handleSave}
            disabled={loading || !hasAnyData()}
            className="flex-1"
            size="lg"
            variant="outline"
          >
            仅保存
          </Button>
          <Button
            onClick={handlePrintOnly}
            disabled={loading || !readyToPrint || !hasAnyData()}
            className="flex-1"
            size="lg"
            variant="outline"
          >
            仅打印
          </Button>
        </div>
        <Button
          onClick={handleClear}
          disabled={loading}
          variant="outline"
          className="flex-1"
          size="lg"
        >
          清空
        </Button>
      </div>

      {/* 消息提示 */}
      {message && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 ${
            message.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            <span>{message.text}</span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setMessage(null)}
              className="text-white hover:bg-white/20"
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
